import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { updateOrderStatus as updateOrderStatusService } from '@/services/orderService';
import type { Order, OrderItem } from '@/types/index';
import { useCachedData, useCacheInvalidation } from './useCache';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG } from '@/stores/cacheConfig';

// Re-export types for convenience
export type { Order, OrderItem };

export const useOrders = (branchId?: string, options?: { lean?: boolean; enabled?: boolean }) => {
  const { user, isStaff, staffSession } = useAuth();
  const { invalidateCache } = useCacheInvalidation();
  const lean = options?.lean ?? false;
  const enabled = options?.enabled ?? true; // Allow external control of fetching

  // Create user context for caching (memoized to prevent dependency issues)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  // Generate cache key (include lean flag to cache separately)
  const cacheKey = userContext && branchId ? `${CacheKeys.orders(userContext, branchId)}${lean ? ':lean' : ''}` : '';

  // Define the fetcher function
  const fetchOrders = useCallback(async (): Promise<Order[]> => {
    // Check if user is authenticated (either owner or staff)
    if (!user && !isStaff) {
      return [];
    }

    // Require a branch ID
    if (!branchId || !userContext) {
      throw new Error('Branch ID is required');
    }

    // For staff users, check if they have access to the branch
    if (isStaff && staffSession) {
      if (staffSession.allowed_branches && staffSession.allowed_branches.length > 0) {
        if (!staffSession.allowed_branches.includes(branchId)) {
          throw new Error('Access denied to this branch');
        }
      }
    }

    try {
      // Lean query for list views - only essential fields for display
      const leanSelect = `
        id,
        branch_id,
        order_number,
        status,
        total_amount,
        created_at,
        updated_at,
        customer_name,
        customer_phone,
        order_type,
        table_number,
        order_items (
          id,
          quantity,
          menu_items (
            name
          )
        )
      `;
      
      // Full query for detail views - all fields with relations
      const fullSelect = `
        id,
        branch_id,
        customer_name,
        customer_phone,
        customer_email,
        order_number,
        status,
        total_amount,
        notes,
        created_at,
        updated_at,
        customer_address,
        order_type,
        table_number,
        payment_method,
        delivery_fee,
        estimated_delivery_time,
        service_amount,
        tax_amount,
        subtotal_amount,
        assigned_driver_id,
        order_items (
          id,
          order_id,
          menu_item_id,
          quantity,
          unit_price,
          total_price,
          notes,
          is_acknowledged,
          is_removed,
          menu_items (
            name,
            description
          )
        ),
        order_customers (
          id,
          customer_name,
          customer_phone,
          customer_email,
          added_at
        ),
        delivery_assignments!delivery_assignments_order_id_fkey (
          id,
          assignment_status,
          driver_id,
          assigned_at,
          accepted_at,
          picked_up_at,
          in_transit_at,
          delivered_at,
          driver:delivery_drivers (
            id,
            name,
            phone,
            current_status
          )
        )
      `;
      
      const query = supabase
        .from('orders')
        .select(lean ? leanSelect : fullSelect)
        .eq('branch_id', branchId)
        .order('created_at', { ascending: false })
        .limit(lean ? 50 : 100); // Lower limit for lean queries (list view)

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error fetching orders:', error);
        throw error;
      }
      
      console.log(`📊 Fetched ${(data as unknown as Order[])?.length || 0} orders for branch: ${branchId} (lean: ${lean})`);
      return (data as unknown as Order[]) || [];
    } catch (err: unknown) {
      console.error('Error fetching orders:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      throw new Error(errorMessage);
    }
  }, [user, isStaff, staffSession, branchId, userContext, lean]);

  // Use cached data hook with shorter TTL for orders (dynamic data)
  const {
    data: orders,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData
  } = useCachedData<Order[]>(
    cacheKey,
    fetchOrders,
    {
      enabled: enabled && !!branchId && !!userContext, // Respect external enabled flag
      ttl: TTL_CONFIG.orders, // 5 minutes for orders
      fallbackToCache: true,
      backgroundRefresh: true // Keep orders fresh in background
    }
  );

  // Optimistic overlay for quick UI responsiveness on status updates
  const [optimisticById, setOptimisticById] = useState<Record<string, Partial<Order>>>({})
  const [updatingOrderIds, setUpdatingOrderIds] = useState<Set<string>>(new Set())
  
  // Track previous optimisticById to detect changes
  const prevOptimisticRef = useRef(optimisticById);

  // Auto-clear optimistic updates when server data matches (in useEffect, not useMemo)
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    if (!optimisticById || Object.keys(optimisticById).length === 0) return;
    
    const idsToRemove: string[] = [];
    
    for (const orderId in optimisticById) {
      const serverOrder = orders.find(o => o.id === orderId);
      const optimisticUpdate = optimisticById[orderId];
      
      // If server data matches optimistic update, mark for removal
      if (serverOrder && optimisticUpdate.status && serverOrder.status === optimisticUpdate.status) {
        console.log(`🔄 Server data matches optimistic update for order ${orderId}, clearing optimistic state`);
        idsToRemove.push(orderId);
      }
    }
    
    // Batch update if there are any to remove
    if (idsToRemove.length > 0) {
      setOptimisticById(prev => {
        const updated = { ...prev };
        for (const id of idsToRemove) {
          delete updated[id];
        }
        return updated;
      });
    }
  }, [orders, optimisticById]);

  // Merge base orders with optimistic overlays (pure computation, no side effects)
  const mergedOrders: Order[] = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    if (!optimisticById || Object.keys(optimisticById).length === 0) return orders;
    
    return orders.map(o => optimisticById[o.id] ? { ...o, ...optimisticById[o.id] } as Order : o);
  }, [orders, optimisticById]);

  const updateOrderStatus = async (orderId: string, status: string) => {
    try {
      setUpdatingOrderIds(prev => new Set(prev).add(orderId))
      // Optimistic update: reflect status immediately
      const nowIso = new Date().toISOString()
      setOptimisticById(prev => ({ ...prev, [orderId]: { status, updated_at: nowIso } }))

      // Server update (does not block UI)
      const result = await updateOrderStatusService(orderId, status, isStaff)

      if (result.success) {
        // Keep optimistic update active and let auto-clearing logic handle it
        // when server data arrives with matching status
        
        // Optimized background reconciliation - reduced cache invalidation
        // Only invalidate table session summary, let optimistic updates handle immediate UI
        void invalidateCache(['tableSessionSummary'], branchId)
        
        // Note: Optimistic update will be automatically cleared when server data 
        // arrives with matching status via the mergedOrders useMemo logic
      } else {
        // Revert optimistic change on failure
        setOptimisticById(prev => {
          const { [orderId]: _removed, ...rest } = prev
          return rest
        })
      }

      return result
    } catch (err: unknown) {
      console.error('Error updating order status:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      console.error('Full error details:', {
        orderId,
        status,
        error: err,
        message: errorMessage
      });
      // On unexpected error, ensure optimistic overlay is cleared
      setOptimisticById(prev => {
        const { [orderId]: _removed, ...rest } = prev
        return rest
      })
      return { success: false, error: errorMessage };
    }
    finally {
      setUpdatingOrderIds(prev => {
        const next = new Set(prev)
        next.delete(orderId)
        return next
      })
    }
  };

  const createOrder = async (orderData: {
    branchId: string;
    customerName: string;
    customerPhone: string;
    customerEmail?: string;
    customerAddress?: string;
    notes?: string;
    orderType?: 'pickup' | 'delivery' | 'dine_in';
    tableNumber?: string;
    deliveryZoneId?: string;
    deliveryFee?: number;
    estimatedDeliveryTime?: string;
    items: Array<{
      menuItemId: string;
      variantId?: string;
      quantity: number;
      unitPrice: number;
      notes?: string;
    }>;
    customerId: string;
  }) => {
    try {
      console.time('⏱️ Order Creation Total Time');
      
      // Use the table session-aware order creation function
      const { addItemsToTableSessionOrCreateOrder } = await import('@/services/menuService');
      const order = await addItemsToTableSessionOrCreateOrder(orderData);
      
      console.timeEnd('⏱️ Order Creation Total Time');
      
      // Optimized cache invalidation - don't wait for it
      void invalidateCache(['orders', 'tableSessionSummary'], branchId);
      
      // Delayed refetch to allow optimistic updates
      setTimeout(() => {
        void refetch();
      }, 200);
      
      return { success: true, order };
    } catch (err: unknown) {
      console.error('Error creating order:', err);
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  };

  // Function to clear optimistic updates for a specific order
  const clearOptimisticUpdate = (orderId: string) => {
    setOptimisticById(prev => {
      const { [orderId]: _removed, ...rest } = prev;
      return rest;
    });
    setUpdatingOrderIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
  };

  return {
    orders: mergedOrders,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate: invalidateCachedData,
    updateOrderStatus,
    createOrder,
    clearOptimisticUpdate,
    isOrderUpdating: (id: string) => updatingOrderIds.has(id)
  };
};
